import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Download, Calendar } from 'lucide-react';
import { useLegalDocuments } from '@/hooks/useLegalDocuments';
import { format } from 'date-fns';

const LegalHub = () => {
  const { data: documents, isLoading } = useLegalDocuments();

  const publishedDocuments = documents?.filter(doc => 
    doc.status === 'published' && doc.is_active
  ) || [];

  const getDocumentPath = (type: string) => {
    const paths: Record<string, string> = {
      privacy_policy: '/privacy',
      terms_of_service: '/terms',
      cookie_policy: '/cookies',
      data_protection: '/data-protection',
      user_agreement: '/user-agreement',
      refund_policy: '/refund-policy',
      accessibility_statement: '/accessibility',
      community_guidelines: '/community-guidelines',
    };
    return paths[type] || `/legal/${type}`;
  };

  const formatDocumentType = (type: string) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading legal documents...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold tracking-tight mb-4">Legal Documents</h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Find all our legal documents, policies, and terms that govern your use of our services.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {publishedDocuments.map((document) => (
          <Card key={document.id} className="h-full hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <CardTitle className="text-xl">{document.title}</CardTitle>
                  <Badge variant="outline">
                    {formatDocumentType(document.document_type)}
                  </Badge>
                </div>
                <FileText className="h-6 w-6 text-muted-foreground" />
              </div>
              <CardDescription>
                Version {document.version}
                {document.effective_date && (
                  <div className="flex items-center mt-2 text-sm">
                    <Calendar className="mr-1 h-3 w-3" />
                    Effective: {format(new Date(document.effective_date), 'MMM dd, yyyy')}
                  </div>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Last updated: {format(new Date(document.updated_at), 'MMM dd, yyyy')}
                </p>
                
                <div className="flex space-x-2">
                  <Button asChild className="flex-1">
                    <Link to={getDocumentPath(document.document_type)}>
                      View Document
                    </Link>
                  </Button>
                  
                  {document.pdf_url && (
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => window.open(document.pdf_url, '_blank')}
                      title="Download PDF"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {publishedDocuments.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Legal Documents Available</h3>
            <p className="text-muted-foreground">
              Legal documents will be available here once they are published.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="mt-12 text-center">
        <Card className="bg-muted/50">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-2">Questions About Our Policies?</h3>
            <p className="text-muted-foreground mb-4">
              If you have any questions about our legal documents or policies, please don't hesitate to contact us.
            </p>
            <Button variant="outline" asChild>
              <Link to="/contact">Contact Us</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LegalHub;